import React from 'react';
import {
  Stack,
  Typography,
  Box,
  Divider,
  Chip,
  Button,
  Paper,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  BiCopy, 
  BiInfoCircle, 
  BiCheck, 
  BiError, 
  BiErrorAlt,
  BiCalendar,
  BiUser,
  BiDollar,
  BiBuilding,
  BiHash,
} from 'react-icons/bi';
import { NumericFormat } from 'react-number-format';

const StyledInfoContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const InfoItem = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  minWidth: '120px',
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}));

const AutobiaInformationDisplay = ({
  data = [],
  title = '',
  variant = 'default',
  layout = 'vertical',
  showDividers = true,
  animated = true,
  copyable = false,
  onCopy = () => {},
  maxWidth = '100%',
  elevation = 1,
  ...props
}) => {
  const getFieldIcon = (type) => {
    const iconMap = {
      id: BiHash,
      user: BiUser,
      company: BiBuilding,
      amount: BiDollar,
      currency: BiDollar,
      date: BiCalendar,
      status: BiInfoCircle,
      error: BiError,
      warning: BiErrorAlt,
      success: BiCheck,
    };
    return iconMap[type] || BiInfoCircle;
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = status.toString().toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('approved') || statusLower.includes('completed')) {
      return 'success';
    }
    if (statusLower.includes('error') || statusLower.includes('failed') || statusLower.includes('rejected')) {
      return 'error';
    }
    if (statusLower.includes('warning') || statusLower.includes('pending') || statusLower.includes('processing')) {
      return 'warning';
    }
    return 'primary';
  };

  const formatValue = (item) => {
    const { value, type, format, currency = 'SAR' } = item;

    if (value == null || value === '') {
      return <Typography color="text.disabled">-</Typography>;
    }

    switch (type) {
      case 'currency':
      case 'amount':
        return (
          <NumericFormat
            value={value}
            displayType="text"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            renderText={(formattedValue) => (
              <ValueText>
                {formattedValue} {currency}
              </ValueText>
            )}
          />
        );
      
      case 'date':
        const dateValue = new Date(value);
        return (
          <ValueText>
            {format ? dateValue.toLocaleDateString(undefined, format) : dateValue.toLocaleDateString()}
          </ValueText>
        );
      
      case 'status':
        return (
          <Chip
            label={value}
            color={getStatusColor(value)}
            size="small"
            variant="outlined"
          />
        );
      
      case 'link':
        return (
          <Button
            variant="text"
            size="small"
            onClick={() => item.onClick?.(value)}
            sx={{ p: 0, justifyContent: 'flex-start' }}
          >
            <ValueText 
              color="primary.main"
              sx={{ 
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                maxWidth: '100%'
              }}
            >
              {value}
            </ValueText>
          </Button>
        );
      
      case 'list':
        return (
          <Stack spacing={0.5}>
            {Array.isArray(value) ? value.map((item, index) => (
              <Typography key={index} variant="body2">
                • {item}
              </Typography>
            )) : (
              <ValueText>{value}</ValueText>
            )}
          </Stack>
        );
      
      default:
        return (
          <ValueText 
            sx={{ 
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {value}
          </ValueText>
        );
    }
  };

  const handleCopyValue = (value) => {
    navigator.clipboard.writeText(value.toString());
    onCopy(value);
  };

  const renderInfoItem = (item, index) => {
    const IconComponent = item.icon || getFieldIcon(item.type);
    
    const itemContent = (
      <InfoItem 
        direction={layout === 'horizontal' ? 'row' : 'column'}
        spacing={layout === 'horizontal' ? 2 : 0.5}
        alignItems={layout === 'horizontal' ? 'center' : 'flex-start'}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {item.showIcon !== false && (
            <IconComponent 
              size={16} 
              color="currentColor"
            />
          )}
          <LabelText variant="body2">
            {item.label}:
          </LabelText>
        </Stack>
        
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Box sx={{ flex: 1 }}>
            {formatValue(item)}
          </Box>
          
          {copyable && item.copyable !== false && (
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={() => handleCopyValue(item.value)}
                sx={{ ml: 1 }}
              >
                <BiCopy size={14} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </InfoItem>
    );

    if (animated) {
      return (
        <motion.div
          key={item.key || index}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
        >
          {itemContent}
          {showDividers && index < data.length - 1 && (
            <Divider sx={{ my: 1 }} />
          )}
        </motion.div>
      );
    }

    return (
      <Box key={item.key || index}>
        {itemContent}
        {showDividers && index < data.length - 1 && (
          <Divider sx={{ my: 1 }} />
        )}
      </Box>
    );
  };

  if (variant === 'grid') {
    return (
      <StyledInfoContainer elevation={elevation} sx={{ maxWidth }}>
        {title && (
          <>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </>
        )}
        <Grid container spacing={2}>
          {data.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.key || index}>
              {renderInfoItem(item, index)}
            </Grid>
          ))}
        </Grid>
      </StyledInfoContainer>
    );
  }

  if (variant === 'compact') {
    return (
      <Box sx={{ maxWidth }}>
        {title && (
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            {title}
          </Typography>
        )}
        <Stack spacing={1}>
          {data.map((item, index) => (
            <Stack key={item.key || index} direction="row" justifyContent="space-between" alignItems="center">
              <LabelText variant="body2">{item.label}</LabelText>
              <Box>{formatValue(item)}</Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <StyledInfoContainer elevation={elevation} sx={{ maxWidth }} {...props}>
      {title && (
        <>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      
      <Stack spacing={showDividers ? 0 : 1}>
        {data.map((item, index) => renderInfoItem(item, index))}
      </Stack>
    </StyledInfoContainer>
  );
};

export default AutobiaInformationDisplay;